import { AchievementInfo, AchievementInfos, AchievementRegistraionMaterial, ICertData } from '@interfaces/Achievements'
import { CertificateTypes, DEFAULT_LANGUAGE } from '@utils/constants'
import groupBy from 'lodash/groupBy'
import orderBy from 'lodash/orderBy'
import moment from 'moment'
import { StudentProfileInfo } from '@interfaces/Student'
import { flatten } from 'lodash'

const levelRegEx = /[0-9]{2}|[0-9]/ // material language level is currently derived from material name
const AI_TRAINING_MATCHERS = [
  'ai literacy and compliance training',
  'ai literacy compliance training',
  'ai training certificate',
]

const normalizeCertificateText = (value = '') =>
  value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

class Certificates {
  data: AchievementInfos[]
  studentProfile: StudentProfileInfo

  constructor(data: AchievementInfos[], studentProfile: StudentProfileInfo) {
    this.studentProfile = studentProfile
    this.data = this.initializeRegistrationAttributes(data)
  }

  private initializeRegistrationAttributes(data: AchievementInfos[]) {
    const registrations = this.studentProfile?.RegistrationWrappers
    const initializedData = data.map((cert) => {
      const registration = registrations?.find(
        (reg) => reg.RegistrationInfo.RegistrationId === cert.achievementInfo?.Registration?.Id
      )
      return {
        ...cert,
        achievementInfo: {
          ...cert.achievementInfo,
          Registration: {
            ...cert.achievementInfo?.Registration,
            Language: registration?.RegistrationInfo?.Language,
            LanguageLevel: Number(registration?.RegistrationInfo?.LanguageLevel ?? 0),
            PartialName:
              cert.achievementInfo?.Registration?.RegistrationMaterials?.[0]?.MaterialName?.split(levelRegEx)?.[0],
            // ProgramType: registration?.RegistrationInfo?.ProgramType,
          },
        },
      }
    })

    return this.dedupeAiTrainingAchievements(initializedData)
  }

  private dedupeAiTrainingAchievements(data: AchievementInfos[]) {
    const aiTrainingAchievements = new Map<string, AchievementInfos>()

    return data.reduce((certificates, cert) => {
      const achievement = cert.achievementInfo

      if (!this.isAiTrainingAchievement(achievement)) {
        certificates.push(cert)
        return certificates
      }

      const key = this.getAiTrainingDedupeKey(achievement)
      const existing = aiTrainingAchievements.get(key)

      if (!existing) {
        aiTrainingAchievements.set(key, cert)
        certificates.push(cert)
        return certificates
      }

      if (this.shouldPreferAiTrainingAchievement(achievement, existing.achievementInfo)) {
        aiTrainingAchievements.set(key, cert)
        const existingIndex = certificates.findIndex((item) => item === existing)
        if (existingIndex >= 0) certificates[existingIndex] = cert
      }

      return certificates
    }, [] as AchievementInfos[])
  }

  private shouldPreferAiTrainingAchievement(next?: AchievementInfo, current?: AchievementInfo) {
    const nextHasRequiredData = this.hasRequiredAiTrainingData(next)
    const currentHasRequiredData = this.hasRequiredAiTrainingData(current)

    if (nextHasRequiredData !== currentHasRequiredData) return nextHasRequiredData

    const nextHasReference = Boolean(next?.ReferenceId)
    const currentHasReference = Boolean(current?.ReferenceId)

    if (nextHasReference !== currentHasReference) return nextHasReference

    return this.getAchievementTimestamp(next) > this.getAchievementTimestamp(current)
  }

  private getAchievementTimestamp(achievement?: AchievementInfo) {
    const date = moment(
      achievement?.IssueDate ||
        achievement?.Registration?.Program?.EndDate ||
        achievement?.Registration?.Program?.StartDate
    )

    return date.isValid() ? date.valueOf() : 0
  }

  private getAiTrainingDedupeKey(achievement?: AchievementInfo) {
    return `${this.getStudentName(achievement).toLowerCase()}_ai-training-certificate`
  }

  private hasRequiredAiTrainingData(achievement?: AchievementInfo) {
    return Boolean(
      this.getStudentName(achievement) && this.getCoreMaterialName(achievement, true) && achievement?.ReferenceId
    )
  }

  private isAiTrainingAchievement(achievement?: AchievementInfo) {
    const certificateText = normalizeCertificateText(
      [
        achievement?.Type,
        achievement?.TemplateName,
        achievement?.CertificateProgramName,
        ...(achievement?.Registration?.RegistrationMaterials || []).map((material) => material.MaterialName),
      ]
        .filter(Boolean)
        .join(' ')
    )

    return AI_TRAINING_MATCHERS.some((matcher) => certificateText.includes(matcher))
  }

  private getCertificateProgramName(achievement?: AchievementInfo) {
    return achievement?.CertificateProgramName?.trim()
  }

  private getCoreMaterialName(achievement?: AchievementInfo, isAiTrainingCertificate = false) {
    const certificateProgramName = this.getCertificateProgramName(achievement)
    if (isAiTrainingCertificate && certificateProgramName) return certificateProgramName

    const materials = this.getCertificateMaterials(achievement)
    return materials.find((mat) => !mat.IsElective && !mat.IsTest)?.MaterialName || materials[0]?.MaterialName
  }

  private getCertificateMaterials(achievement?: AchievementInfo): AchievementRegistraionMaterial[] {
    return (
      achievement?.Registration?.RegistrationMaterials?.filter(
        (material) => material.MaterialName && !/MyBerlitz.*Tutor/.test(material.MaterialName)
      ) || []
    )
  }

  private getCertificateMaterialNames(achievement?: AchievementInfo, isAiTrainingCertificate = false) {
    const materialNames = this.getCertificateMaterials(achievement)
      .filter((reg) => !reg.IsTest)
      .map((reg) => reg.MaterialName)
      .filter(Boolean)

    if (materialNames.length) return materialNames

    const certificateProgramName = this.getCertificateProgramName(achievement)
    return isAiTrainingCertificate && certificateProgramName ? [certificateProgramName] : []
  }

  private getStudentName(achievement?: AchievementInfo) {
    return `${achievement?.StudentProfile?.FirstName || ''} ${achievement?.StudentProfile?.LastName || ''}`
      .replace(/\s+/g, ' ')
      .trim()
  }

  private getCertificateDate(achievement?: AchievementInfo, isAiTrainingCertificate = false) {
    const issueDate = moment(achievement?.IssueDate)

    if (isAiTrainingCertificate && issueDate.isValid()) {
      return issueDate.format('Do MMMM YYYY')
    }

    const startDate = moment(achievement?.Registration?.Program?.StartDate)
    const endDate = moment(achievement?.Registration?.Program?.EndDate)

    if (!startDate.isValid() || !endDate.isValid()) {
      return issueDate.isValid() ? issueDate.format('Do MMMM YYYY') : ''
    }

    if (!startDate.isSame(endDate, 'year')) {
      return `${startDate.format('Do MMMM YYYY')} - ${endDate.format('Do MMMM YYYY')}`
    }

    return `${startDate.format('Do MMMM')} - ${endDate.format('Do MMMM YYYY')}`
  }

  private getCertificateType(achievement?: AchievementInfo, isAiTrainingCertificate = false) {
    return isAiTrainingCertificate ? CertificateTypes.COMPLETION : achievement?.Type
  }

  private getCertificateDescription1(achievement?: AchievementInfo, isAiTrainingCertificate = false) {
    if (isAiTrainingCertificate) return 'Completed the following program'
    return achievement?.Type === CertificateTypes.ACHIEVEMENT ? 'Berlitz certifies that' : ''
  }

  private getCertificateDescription2(achievement?: AchievementInfo, isAiTrainingCertificate = false) {
    if (isAiTrainingCertificate) return 'Successfully completed the following course'
    return achievement?.Type === CertificateTypes.ACHIEVEMENT
      ? 'successfully completed the following program'
      : 'participated in the following program'
  }

  private getCertificateTemplateName(achievement?: AchievementInfo, isAiTrainingCertificate = false) {
    if (isAiTrainingCertificate) return undefined
    return achievement?.TemplateName === 'Curriculum Test Passed' ? 'Curriculum Test - Passed' : undefined
  }

  private getCertificateLink(achievement?: AchievementInfo) {
    return process.env.CERTIFICATE_URL && achievement?.ReferenceId
      ? `${process.env.CERTIFICATE_URL}?id=${achievement.ReferenceId}`
      : ''
  }

  private getCertificateLinkLabel(achievement?: AchievementInfo) {
    return process.env.CERTIFICATE_URL && achievement?.ReferenceId
      ? `${process.env.CERTIFICATE_URL.replace('https://', '')}?id=${achievement.ReferenceId}`
      : ''
  }

  private hasRequiredCertificateData(cert: ICertData) {
    return Boolean(cert.name && cert.coreName && cert.link && cert.linkLabel)
  }

  get GroupedCertificatesInit() {
    let data = groupBy(this.data, (item) => item.achievementInfo?.Registration?.Id)

    /**
     * Sort by program start date
     */
    data = Object.entries(data)
      .sort(([, a], [, b]) => {
        const startDateA = moment(a[0].achievementInfo?.Registration?.Program?.StartDate)
        const startDateB = moment(b[0].achievementInfo?.Registration?.Program?.StartDate)
        return startDateB.diff(startDateA)
      })
      .reduce((r, [k, v]) => ({ ...r, [k]: v }), {})

    return Object.keys(data)
      .map((key) => {
        return data[key]
          .sort((a, b) => (a.achievementInfo?.TemplateName || '').localeCompare(b.achievementInfo?.TemplateName || ''))
          .map((cert): ICertData => {
            const temp = cert.achievementInfo
            const isAiTrainingCertificate = this.isAiTrainingAchievement(temp)

            return {
              date: this.getCertificateDate(temp, isAiTrainingCertificate),
              id: temp?.Id,
              registrationId: temp?.Registration?.Id,
              coreName: this.getCoreMaterialName(temp, isAiTrainingCertificate),
              type: this.getCertificateType(temp, isAiTrainingCertificate),
              templateName: this.getCertificateTemplateName(temp, isAiTrainingCertificate),
              name: this.getStudentName(temp),
              materials: this.getCertificateMaterialNames(temp, isAiTrainingCertificate),
              signedBy: {
                title: temp?.SignedBy?.DisplayTitle || '',
                signature: temp?.SignedBy?.DisplaySignatureURL || '',
                name: temp?.SignedBy?.DisplayName || '',
              },
              link: this.getCertificateLink(temp),
              linkLabel: this.getCertificateLinkLabel(temp),
              description1: this.getCertificateDescription1(temp, isAiTrainingCertificate),
              description2: this.getCertificateDescription2(temp, isAiTrainingCertificate),
              defaultLanguage: isAiTrainingCertificate ? DEFAULT_LANGUAGE : undefined,
              isAiTrainingCertificate,
              registration: temp?.Registration,
            }
          })
          .filter((cert) => this.hasRequiredCertificateData(cert))
      })
      .filter((certs) => certs.length)
  }

  get GroupedCertificates() {
    /**
     * Group by program start date, level, language and its registration id:
     */
    let achievementsByDateAndLanguage = groupBy(
      this.GroupedCertificatesInit,
      (item) =>
        `${item?.[0]?.registration?.Program?.StartDate}_${item?.[0]?.registration?.Language}_${item?.[0]?.registration?.LanguageLevel}_${item?.[0]?.registrationId}`
    )

    /**
     * Lets convert it into an array of objects so that
     * we can sort it in descending order
     */
    let achievementsInKeyPairs = Object.keys(achievementsByDateAndLanguage).map((key) => ({
      key,
      value: achievementsByDateAndLanguage[key],
    }))
    achievementsInKeyPairs = orderBy(achievementsInKeyPairs, 'key', 'desc')

    return achievementsInKeyPairs
      .map((item) => {
        const certificates = item.value

        return {
          key: item.key,
          value: flatten(certificates),
        }
      })
      .reduce((acc, cur) => {
        acc.push(cur.value)
        return acc
      }, [] as any)
  }
}

export default Certificates
